import { Eye, Pencil } from 'lucide-react';
import type { NotificationTemplateListItem } from '@control-center/shared';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

interface TemplateListProps {
  items: NotificationTemplateListItem[];
  canEdit: boolean;
  onEdit: (id: string) => void;
  onPreview: (id: string) => void;
}

function ChannelBadge({ channel }: { channel: string }) {
  const isEmail = channel === 'email';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] px-2 py-[2px] text-[11px] font-medium',
        isEmail ? 'bg-brand/15 text-brand' : 'bg-good/15 text-good',
      )}
    >
      {isEmail ? 'Email' : 'WhatsApp'}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] px-2 py-[2px] text-[11px] font-medium',
        active ? 'bg-good/15 text-good' : 'bg-muted/20 text-muted',
      )}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

export function TemplateList({
  items,
  canEdit,
  onEdit,
  onPreview,
}: TemplateListProps): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="rounded-[12px] border border-line bg-panel py-12 text-center text-sm text-muted">
        Nenhum template encontrado com esses filtros.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[12px] border border-line bg-panel">
      <table className="w-full text-sm">
        <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
          <tr>
            <th className="px-4 py-3 font-medium">Key</th>
            <th className="px-4 py-3 font-medium">Nome</th>
            <th className="px-4 py-3 font-medium">Canal</th>
            <th className="px-4 py-3 font-medium">Variante</th>
            <th className="px-4 py-3 font-medium text-right">Versão</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Atualizado em</th>
            <th className="px-4 py-3 font-medium text-right">Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((item) => (
            <tr key={item.id} className="text-ink hover:bg-panel-2">
              <td className="px-4 py-3 font-mono text-[12px] text-ink">{item.key}</td>
              <td className="px-4 py-3">{item.name}</td>
              <td className="px-4 py-3">
                <ChannelBadge channel={item.channel} />
              </td>
              <td className="px-4 py-3 text-muted">
                {item.variantType ? item.variantType.toUpperCase() : '—'}
              </td>
              <td className="px-4 py-3 text-right font-mono tnum text-muted">v{item.version}</td>
              <td className="px-4 py-3">
                <ActiveBadge active={item.isActive} />
              </td>
              <td className="px-4 py-3 text-muted-2 tnum">{formatBRDateTime(item.updatedAt)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onPreview(item.id)}
                    className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 text-[12px] text-ink hover:bg-panel-3"
                    aria-label={`Preview ${item.key}`}
                  >
                    <Eye size={13} strokeWidth={1.8} />
                    Preview
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(item.id)}
                      className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 text-[12px] text-ink hover:bg-panel-3"
                      aria-label={`Editar ${item.key}`}
                    >
                      <Pencil size={13} strokeWidth={1.8} />
                      Editar
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
